import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { HomeSummary, Me } from '../../services/src/shared/api-types.ts';
import type { Api } from './api.ts';
import type { Auth } from './auth.ts';
import { num, shortStamp } from './format.ts';
import { onPlaidReturnPath } from './plaid.ts';
import { Activity } from './screens/Activity.tsx';
import { ConnectionsScreen } from './screens/Connections.tsx';
import { Home } from './screens/Home.tsx';
import { Payments } from './screens/Payments.tsx';
import { Review } from './screens/Review.tsx';
import { Unmatched } from './screens/Unmatched.tsx';
import { BootScreen } from './ui.tsx';

export type Screen = 'home' | 'review' | 'unmatched' | 'payments' | 'connections' | 'activity';

/** Icons for the phone tab bar (hidden on wide screens). */
const ICONS: Record<Screen, string> = {
  home: 'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  review: 'M9 11l3 3 8-8M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9',
  unmatched: 'M4 6h16M4 12h10M4 18h6',
  payments: 'M12 3v18M16.5 7H10a3 3 0 0 0 0 6h4a3 3 0 0 1 0 6H7',
  connections: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 10h18',
  activity: 'M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
};

function NavIcon({ screen }: { screen: Screen }) {
  return (
    <svg className="nav-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICONS[screen]} />
    </svg>
  );
}

function initials(email: string | null | undefined): string {
  const name = (email ?? '').split('@')[0];
  const parts = name.split(/[._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '·';
}

const SCREENS: { id: Screen; label: string; adminOnly?: boolean }[] = [
  { id: 'home', label: 'Home' },
  { id: 'review', label: 'Review' },
  { id: 'unmatched', label: 'Unmatched' },
  // "Salesforce payments" wraps in the sidebar; the screen's heading says it in full.
  { id: 'payments', label: 'Payments' },
  { id: 'connections', label: 'Connections', adminOnly: true },
  { id: 'activity', label: 'Activity' },
];

function screenFromHash(): Screen {
  // A bank's OAuth sign-in comes back to /plaid-return; Connections picks Link up again.
  if (onPlaidReturnPath() && !location.hash) return 'connections';
  // Salesforce payments used to be a tab on Unmatched; old links land on its Unmatched view.
  if (location.hash.startsWith('#/unmatched/payments')) history.replaceState(null, '', '#/payments/unmatched');
  const id = location.hash.replace(/^#\/?/, '').split('/')[0] as Screen;
  return SCREENS.some((s) => s.id === id) ? id : 'home';
}

/** What every screen gets from the shell. */
export interface ScreenProps {
  api: Api;
  summary: HomeSummary | null;
  summaryError: string | null;
  /** Re-reads the counts in the sidebar and on Home (after a decision or a new login). */
  refreshSummary: () => void;
  isAdmin: boolean;
  /** Mock mode (npm run dev -w web): Plaid Link is simulated in the page. */
  mock: boolean;
}

export function App({ api, auth, mock }: { api: Api; auth: Auth; mock: boolean }) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Phone layout: the account menu (email, sync, sign out) behind the initials button. */
  const [accountOpen, setAccountOpen] = useState(false);
  useEffect(() => {
    if (!accountOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setAccountOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [accountOpen]);
  const [screen, setScreen] = useState<Screen>(screenFromHash);
  const [summary, setSummary] = useState<HomeSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    api.me().then(setMe, (e: Error) => setError(e.message));
    const onHash = () => setScreen(screenFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [api]);

  const isAdmin = Boolean(me?.groups.includes('admin'));
  const canReview = isAdmin || Boolean(me?.groups.includes('reviewer'));

  const refreshSummary = useCallback(() => {
    api.home().then(
      (s) => {
        setSummary(s);
        setSummaryError(null);
      },
      (e: Error) => setSummaryError(e.message),
    );
  }, [api]);

  useEffect(() => {
    if (canReview) refreshSummary();
  }, [canReview, refreshSummary]);

  const visible = SCREENS.filter((s) => !s.adminOnly || isAdmin);
  const current = visible.some((s) => s.id === screen) ? screen : 'home';
  const label = visible.find((s) => s.id === current)?.label ?? 'Home';

  // On navigation, move focus to the content so keyboard and screen reader users land on the new screen.
  useEffect(() => {
    document.title = `${label} · Four Winds Sync`;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    mainRef.current?.focus();
  }, [current, label]);

  if (error) return <p className="fatal" role="alert">{error}</p>;
  if (!me) return <BootScreen />;

  const reviewCount = summary?.review.count;
  const unmatchedCount = summary?.unmatched.lines.count;
  const paymentsCount = summary?.unmatched.payments.count;
  const attention = summary?.connections.totals.attentionLogins ?? 0;
  const lastSync = summary?.lastSync;

  const props: ScreenProps = { api, summary, summaryError, refreshSummary, isAdmin, mock };

  return (
    <div className="shell">
      <a className="skip" href="#content" onClick={(e) => (e.preventDefault(), mainRef.current?.focus())}>
        Skip to content
      </a>
      <nav aria-label="Main" className="side">
        <div className="brand">
          <strong>Four Winds</strong>
          <span className="brand-tag">Payment matching</span>
          {lastSync && <span className="brand-sync">{lastSync.ok ? `Synced ${shortStamp(lastSync.at)}` : `Sync failed ${shortStamp(lastSync.at)}`}</span>}
        </div>
        <button
          type="button"
          className="account-btn"
          aria-expanded={accountOpen}
          aria-controls="account-menu"
          aria-label={`Account: ${me.email ?? auth.profile().username ?? 'signed in'}`}
          onClick={() => setAccountOpen((o) => !o)}
        >
          {initials(me.email ?? auth.profile().email)}
        </button>
        <div className="side-nav" style={{ '--tabs': visible.length } as CSSProperties}>
          {visible.map((s) => (
            <a key={s.id} href={`#/${s.id}`} aria-current={s.id === current ? 'page' : undefined} onClick={() => setAccountOpen(false)}>
              <NavIcon screen={s.id} />
              <span className="nav-label">{s.label}</span>
              {s.id === 'review' && reviewCount !== undefined && (
                <span className="nav-count">
                  {num(reviewCount)}
                  <span className="visually-hidden"> waiting</span>
                </span>
              )}
              {s.id === 'unmatched' && unmatchedCount !== undefined && (
                <span className="nav-count nav-count-quiet">
                  {num(unmatchedCount)}
                  <span className="visually-hidden"> bank lines</span>
                </span>
              )}
              {s.id === 'payments' && paymentsCount !== undefined && (
                <span className="nav-count nav-count-quiet">
                  {num(paymentsCount)}
                  <span className="visually-hidden"> unmatched</span>
                </span>
              )}
              {s.id === 'connections' && attention > 0 && (
                <span className="nav-badge">
                  {attention}
                  <span className="visually-hidden"> {attention === 1 ? 'login needs' : 'logins need'} attention</span>
                </span>
              )}
            </a>
          ))}
        </div>
        <div className={`side-foot${accountOpen ? ' open' : ''}`} id="account-menu">
          {mock && <span className="mock-flag">Mock data</span>}
          {lastSync && <span>{lastSync.ok ? `Last sync ${shortStamp(lastSync.at)}` : `Last sync failed ${shortStamp(lastSync.at)}`}</span>}
          {summary && !lastSync && <span>No sync yet</span>}
          <span className="email">{me.email ?? auth.profile().username ?? 'Signed in'}</span>
          <button type="button" onClick={() => auth.signOut()}>
            Sign out
          </button>
        </div>
      </nav>
      <main className="main" id="content" ref={mainRef} tabIndex={-1}>
        {!canReview ? (
          <div className="page">
            <h1 className="page-title">Four Winds Sync</h1>
            <p className="state">Your account isn't in a group yet. Ask an admin to add you to reviewer or admin.</p>
          </div>
        ) : current === 'home' ? (
          <Home {...props} />
        ) : current === 'review' ? (
          <Review {...props} />
        ) : current === 'unmatched' ? (
          <Unmatched {...props} />
        ) : current === 'payments' ? (
          <Payments {...props} />
        ) : current === 'connections' ? (
          <ConnectionsScreen {...props} />
        ) : (
          <Activity {...props} />
        )}
      </main>
    </div>
  );
}
